import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { Modal } from 'flowbite';

import ServiceCard from './ServiceCard';
import ServiceUpsertModal from './ServiceUpsertModal';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import TableSearchInput from 'CommonComponents/TableSearchInput';

import CirclePlusIcon from 'Assets/icons/circle-plus.svg';
import FileIcon from 'Assets/icons/file.svg';

const UPSERT_SERVICE_MODAL_ID = 'upsert-service-modal';
const DELETE_SERVICE_MODAL_ID = 'delete-service-modal';

export default function ServicesIndex() {
    const [services, setServices] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [query, setQuery] = useState({ search: '' });

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [serviceUpsertModalElement, setServiceUpsertModalElement] = useState(null);
    const [serviceDeleteModalElement, setServiceDeleteModalElement] = useState(null);

    const serviceUpsertModalRef = useRef();
    const serviceDeleteModalRef = useRef();

    useEffect(() => {
        fetchServices();
        serviceUpsertModalRef && setServiceUpsertModalElement(new Modal(serviceUpsertModalRef.current, modalOptions(false)));
        serviceDeleteModalRef && setServiceDeleteModalElement(new Modal(serviceDeleteModalRef.current, modalOptions(false)));
    }, []);

    const fetchServices = () => {
        setIsLoading(true);

        axios
            .get(fetchServicesUrl, { params: { ...query } })
            .then(({ data }) => {
                setServices(data);
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => setIsLoading(false));
    };

    const onServiceUpsert = (service = null) => {
        setSelectedService(service);
        showModalElemenet(serviceUpsertModalElement);
    };

    const onServiceDelete = (service) => {
        setSelectedService(service);
        showModalElemenet(serviceDeleteModalElement);
    };

    const handleSearch = (event) => {
        const search = event?.target?.value;
        setQuery((query) => ({ ...query, search }));
    };

    const handleSearchKeyPress = (event) => {
        if (event.key == 'Enter') {
            fetchServices();
        }
    };

    const showModalElemenet = (modalElement) => {
        modalElement && modalElement.show();
    };

    const hideModalElemenet = (modalElement) => {
        modalElement && modalElement.hide();
    };

    const cleanConfirmationModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);

    const triggerDeleteService = () => {
        setActionInProgress(true);

        const url = deleteServiceUrl.replace('SERVICE_ID', selectedService?.id);
        axios
            .delete(url)
            .then(() => {
                fetchServices();
                hideModalElemenet(serviceDeleteModalElement);
            })
            .catch((e) => {
                setActionFailed(true);
            })
            .finally(() => setActionCompleted(true));
    };

    return (
        <div>
            <div className="row position-relative">
                <div className="col-6 offset-6 mt-n3 position-absolute search-ontop">
                    <div className="row">
                        <TableSearchInput searchValue={query.search} handleChange={handleSearch} handleKeyPress={handleSearchKeyPress} />
                        <div className="col-auto">
                            <a className="button primary tw-mr-3" href={importServicesUrl} data-action="navigate">
                                <FileIcon className={'tw-w-3.5 tw-h-3.5 tw-text-white'} />
                                {$t('Import from file')}
                            </a>
                            <Button small variant="primary" className="tw-mr-3" type="button" onClick={onServiceUpsert}>
                                <CirclePlusIcon className={'tw-w-3.5 tw-h-3.5 tw-text-white'} /> {$t('Add new service')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {!isLoading && !!services?.length && (
                <div className="tw-grid tw-grid-cols-6 max-2xl:tw-grid-cols-4 max-lg:tw-grid-cols-2 tw-gap-3">
                    {services.map((service) => (
                        <div key={service.id} className="text-base-tile tw-group tw-cursor-pointer tw-relative tw-p-1.5">
                            <ServiceCard service={service} onEdit={onServiceUpsert} onDelete={onServiceDelete} />
                        </div>
                    ))}
                </div>
            )}
            {isLoading && <div>{$t('Loading')}...</div>}
            {!isLoading && services?.length === 0 && <div>{$t('No services found')}</div>}

            <ServiceUpsertModal
                modalId={UPSERT_SERVICE_MODAL_ID}
                modalRef={serviceUpsertModalRef}
                modalElement={serviceUpsertModalElement}
                service={selectedService}
                storeUrl={storeServiceUrl}
                updateUrl={updateServiceUrl}
                loading={isLoading}
                onSuccess={() => fetchServices()}
            />
            <ConfirmationModal
                triggerAction={triggerDeleteService}
                cleanStates={cleanConfirmationModalStates}
                allowAction={selectedService != null}
                modalId={DELETE_SERVICE_MODAL_ID}
                modalRef={serviceDeleteModalRef}
                modalElement={serviceDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: selectedService?.name })}
                actionCompletedMsg=""
                actionFailedMsg={$t('{{name}} could not be deleted', { name: selectedService?.name })}
                title={$t('Remove Service {{name}}', { name: selectedService?.name })}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />
        </div>
    );
}

if (document.getElementById('ServicesIndex')) {
    var rootEl = document.getElementById('ServicesIndex');
    ReactDOM.render(
        <App>
            <ServicesIndex />
        </App>,
        rootEl,
    );
}
